import { useState, useEffect, useCallback } from 'react';
import { beratungAPI } from '@/lib/api';
import { Calendar, Clock, MapPin, CheckCircle2, ChevronLeft, Loader2, Download, Mail } from 'lucide-react';

// Öffentliche, login-freie Buchungsseite für die kostenlose Erstberatung.
// Zugang per QR-Code → mobil-first, große Touch-Ziele, P1-Branding.
const P1_RED = '#d1272e';

const WOCHENTAGE = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];
const MONATE = ['Jan.', 'Feb.', 'März', 'Apr.', 'Mai', 'Juni', 'Juli', 'Aug.', 'Sep.', 'Okt.', 'Nov.', 'Dez.'];

function fmtDay(dateStr) {
  try {
    const d = new Date(dateStr + 'T00:00:00');
    return { wd: WOCHENTAGE[(d.getDay() + 6) % 7], dm: `${d.getDate()}. ${MONATE[d.getMonth()]}` };
  } catch { return { wd: '', dm: dateStr }; }
}

export default function BeratungBuchungPage() {
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(true);
  const [days, setDays] = useState([]);
  const [dauer, setDauer] = useState(15);
  const [ort, setOrt] = useState('');
  const [step, setStep] = useState('slots'); // slots | form | done
  const [selDate, setSelDate] = useState(null);
  const [selStart, setSelStart] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '', email: '', anliegen: '' });
  const [honeypot, setHoneypot] = useState('');
  const [privacy, setPrivacy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const loadSlots = useCallback(async () => {
    setLoading(true);
    try {
      const [cfgRes, slotsRes] = await Promise.all([beratungAPI.getConfig(), beratungAPI.getSlots()]);
      setEnabled(!!cfgRes.data.enabled && !!slotsRes.data.enabled);
      setOrt(cfgRes.data.ort || '');
      setDauer(cfgRes.data.dauer_minuten || 15);
      setDays(slotsRes.data.days || []);
    } catch {
      setEnabled(false);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadSlots(); }, [loadSlots]);

  const pickSlot = (date, start) => {
    setSelDate(date); setSelStart(start); setError(''); setStep('form');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const submit = async () => {
    setError('');
    if (!form.name.trim() || form.name.trim().length < 2) { setError('Bitte gib deinen Namen an.'); return; }
    if (!form.phone.trim()) { setError('Bitte gib deine Telefonnummer an.'); return; }
    if (!/^[^@\s]+@[^@\s]+\.[a-zA-Z]{2,}$/.test(form.email.trim())) { setError('Bitte gib eine gültige E-Mail-Adresse an.'); return; }
    if (!privacy) { setError('Bitte akzeptiere die Datenschutzerklärung.'); return; }
    setSubmitting(true);
    try {
      const res = await beratungAPI.buchen({
        date: selDate, start: selStart,
        name: form.name.trim(), phone: form.phone.trim(), email: form.email.trim(),
        anliegen: form.anliegen.trim(),
        privacy_accepted: true,
        website: honeypot, // Honeypot — bleibt bei Menschen leer
      });
      setResult(res.data);
      setStep('done');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      if (err.response?.status === 409) {
        setError('Dieser Termin wurde gerade vergeben. Bitte wähle einen anderen Slot.');
        setStep('slots');
        loadSlots();
      } else {
        setError(err.response?.data?.detail || 'Buchung fehlgeschlagen. Bitte versuche es erneut.');
      }
    }
    setSubmitting(false);
  };

  const selFmt = selDate ? fmtDay(selDate) : null;

  return (
    <div className="min-h-screen bg-gray-50" style={{ fontFamily: "'Manrope', sans-serif" }} data-testid="beratung-page">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-4 flex items-center justify-center">
        <img src="/logo-black.png" alt="P1 Performance Center" className="h-9 object-contain" />
      </div>

      <div className="max-w-md mx-auto px-4 py-6 pb-16">
        {loading ? (
          <div className="text-center py-24">
            <Loader2 className="w-8 h-8 animate-spin mx-auto" style={{ color: P1_RED }} />
            <p className="text-sm text-gray-500 mt-3">Termine werden geladen…</p>
          </div>
        ) : !enabled ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center mt-8" data-testid="beratung-disabled">
            <Calendar className="w-10 h-10 mx-auto text-gray-300 mb-3" />
            <h1 className="text-lg font-bold">Derzeit keine Online-Buchung möglich</h1>
            <p className="text-sm text-gray-500 mt-2">Ruf uns gerne an oder komm direkt vorbei — wir finden einen Termin für dich.</p>
          </div>
        ) : step === 'slots' ? (
          <>
            <div className="text-center mb-6">
              <h1 className="text-2xl font-extrabold tracking-tight">Kostenlose Erstberatung</h1>
              <p className="text-sm text-gray-500 mt-1">{dauer} Minuten · unverbindlich · persönlich</p>
              {ort && <p className="text-xs text-gray-400 mt-1 flex items-center justify-center gap-1"><MapPin className="w-3.5 h-3.5" />{ort}</p>}
            </div>

            {error && <div className="mb-4 text-sm text-white rounded-xl px-4 py-3" style={{ backgroundColor: P1_RED }} data-testid="beratung-error">{error}</div>}

            {days.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center">
                <p className="text-sm text-gray-500">Aktuell sind alle Termine vergeben. Schau bald wieder vorbei!</p>
              </div>
            ) : (
              <div className="space-y-4" data-testid="beratung-days">
                {days.map(day => {
                  const f = fmtDay(day.date);
                  return (
                    <div key={day.date} className="bg-white rounded-2xl border border-gray-200 p-4">
                      <div className="flex items-baseline gap-2 mb-3">
                        <span className="text-sm font-bold">{f.wd}</span>
                        <span className="text-xs text-gray-400">{f.dm}</span>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        {day.slots.map(s => (
                          <button key={s} onClick={() => pickSlot(day.date, s)} data-testid={`slot-${day.date}-${s}`}
                            className="py-3 rounded-xl border-2 border-gray-200 text-sm font-bold hover:border-current transition-colors active:scale-95"
                            style={{ color: '#111' }}
                            onMouseOver={e => { e.currentTarget.style.borderColor = P1_RED; e.currentTarget.style.color = P1_RED; }}
                            onMouseOut={e => { e.currentTarget.style.borderColor = ''; e.currentTarget.style.color = '#111'; }}>
                            {s}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        ) : step === 'form' ? (
          <>
            <button onClick={() => setStep('slots')} className="flex items-center gap-1 text-sm text-gray-500 mb-4" data-testid="beratung-back">
              <ChevronLeft className="w-4 h-4" /> Anderen Termin wählen
            </button>

            <div className="bg-white rounded-2xl border-2 p-4 mb-5 flex items-center gap-3" style={{ borderColor: P1_RED }}>
              <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: P1_RED + '15' }}>
                <Calendar className="w-5 h-5" style={{ color: P1_RED }} />
              </div>
              <div>
                <div className="text-sm font-bold">{selFmt?.wd}, {selFmt?.dm}</div>
                <div className="text-xs text-gray-500 flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{selStart} Uhr · {dauer} Minuten</div>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-4">
              <div>
                <label className="text-xs font-semibold text-gray-600 block mb-1">Name *</label>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="beratung-name"
                  className="w-full h-12 px-4 border border-gray-300 rounded-xl text-base" placeholder="Vor- und Nachname" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 block mb-1">Telefon *</label>
                <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} type="tel" data-testid="beratung-phone"
                  className="w-full h-12 px-4 border border-gray-300 rounded-xl text-base" placeholder="z. B. 0176 12345678" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 block mb-1">E-Mail *</label>
                <input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} type="email" data-testid="beratung-email"
                  className="w-full h-12 px-4 border border-gray-300 rounded-xl text-base" placeholder="dein@email.de" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 block mb-1">Dein Anliegen (optional)</label>
                <textarea value={form.anliegen} onChange={e => setForm(f => ({ ...f, anliegen: e.target.value }))} rows={3} data-testid="beratung-anliegen"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl text-base resize-none" placeholder="Was möchtest du erreichen?" />
              </div>

              {/* Honeypot — für Menschen unsichtbar, Bots füllen es aus */}
              <div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }} aria-hidden="true">
                <label>Website<input value={honeypot} onChange={e => setHoneypot(e.target.value)} tabIndex={-1} autoComplete="off" /></label>
              </div>

              <label className="flex items-start gap-3 cursor-pointer" data-testid="beratung-privacy">
                <input type="checkbox" checked={privacy} onChange={e => setPrivacy(e.target.checked)}
                  className="mt-1 w-5 h-5 flex-shrink-0" style={{ accentColor: P1_RED }} />
                <span className="text-xs text-gray-500">
                  Ich habe die <a href="/datenschutz" target="_blank" rel="noopener noreferrer" className="underline font-medium" style={{ color: P1_RED }}>Datenschutzerklärung</a> gelesen
                  und bin mit der Verarbeitung meiner Daten zur Terminvereinbarung einverstanden. *
                </span>
              </label>

              {error && <div className="text-sm text-white rounded-xl px-4 py-3" style={{ backgroundColor: P1_RED }} data-testid="beratung-form-error">{error}</div>}

              <button onClick={submit} disabled={submitting} data-testid="beratung-submit"
                className="w-full h-14 rounded-xl text-white text-base font-extrabold transition-opacity disabled:opacity-50 active:scale-[0.99]"
                style={{ backgroundColor: P1_RED }}>
                {submitting ? 'Wird gebucht…' : 'Termin verbindlich buchen'}
              </button>
              <p className="text-[11px] text-gray-400 text-center">Kostenlos & unverbindlich — du kannst jederzeit absagen.</p>
            </div>
          </>
        ) : (
          <div className="text-center pt-6" data-testid="beratung-success">
            <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-9 h-9 text-emerald-500" />
            </div>
            <h1 className="text-2xl font-extrabold">Termin bestätigt!</h1>
            <p className="text-sm text-gray-500 mt-1">Wir freuen uns auf dich.</p>

            <div className="bg-white rounded-2xl border border-gray-200 p-5 mt-6 text-left space-y-3">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 flex-shrink-0" style={{ color: P1_RED }} />
                <div>
                  <div className="text-sm font-bold">{fmtDay(result?.date).wd}, {fmtDay(result?.date).dm}</div>
                  <div className="text-xs text-gray-500">{result?.start} – {result?.end} Uhr</div>
                </div>
              </div>
              {result?.ort && (
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 flex-shrink-0" style={{ color: P1_RED }} />
                  <div className="text-sm">{result.ort}</div>
                </div>
              )}
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 flex-shrink-0" style={{ color: P1_RED }} />
                <div className="text-xs text-gray-500">Du erhältst eine Bestätigungs-E-Mail mit allen Details und einem Absage-Link.</div>
              </div>
            </div>

            {result?.ics_token && (
              <a href={beratungAPI.icsUrl(result.ics_token)} data-testid="beratung-ics"
                className="mt-4 inline-flex items-center gap-2 px-5 h-12 rounded-xl border-2 text-sm font-bold"
                style={{ borderColor: P1_RED, color: P1_RED }}>
                <Download className="w-4 h-4" /> Zum Kalender hinzufügen
              </a>
            )}
          </div>
        )}

        <p className="text-center text-[10px] text-gray-300 mt-10">
          P1 Performance Center · <a href="/impressum" className="underline">Impressum</a> · <a href="/datenschutz" className="underline">Datenschutz</a>
        </p>
      </div>
    </div>
  );
}
